import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { Search, Printer, ArrowLeft, Plus, Save } from "lucide-react";
import "./Billing.css";
import Navbar from "../components/Navbar";

export default function Billing() {
  const navigate = useNavigate();
  const { tableId } = useParams(); 
  
  const [searchQuery, setSearchQuery] = useState("");
  const [gst, setGst] = useState(false);
  const [billItems, setBillItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [showReceipt, setShowReceipt] = useState(false);
  const [menuItems, setMenuItems] = useState([]);

  // 1. Fetch Menu from Database
  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/menu.php`);
        setMenuItems(res.data);
      } catch (err) {
        console.error("Error loading menu from DB");
      }
    };
    fetchMenu();

    if (tableId) {
      const allOrders = JSON.parse(localStorage.getItem("activeTableOrders") || "{}");
      if (allOrders[tableId]) {
        setBillItems(allOrders[tableId]);
      }
    }
  }, [tableId]);

  const subtotal = billItems.reduce((acc, i) => acc + (i.price * i.qty), 0);
  const gstAmount = subtotal * 0.05;
  const grandTotal = gst ? subtotal + gstAmount : subtotal;

  // 2. Settle Bill (Saves to MySQL 'sales' table)
  const handleSettleBill = async () => {
    let nextNumber = 1;
    try {
      const res = await axios.get(`${API_BASE_URL}/reports.php`);
      nextNumber = res.data.length + 1;
    } catch (e) { console.error(e); }

    const paddedNumber = nextNumber.toString().padStart(4, '0');
    const generatedId = tableId && tableId !== "Walk-in" 
      ? `T${tableId}-${paddedNumber}` 
      : `S-WALK-${paddedNumber}`;

    const billData = {
      id: generatedId,
      tableId: tableId || "Walk-in",
      itemsCount: billItems.reduce((acc, item) => acc + item.qty, 0),
      items: JSON.stringify(billItems), 
      subtotal: subtotal,
      gstAmount: gst ? gstAmount : 0,
      finalAmount: Math.round(grandTotal),
      userId: localStorage.getItem("user_id") || 0 // Tag bill with current user
    };

    try {
      const res = await axios.post(`${API_BASE_URL}/billing.php`, billData);
      if (res.data.status === "success") {
        if (tableId) {
          const allOrders = JSON.parse(localStorage.getItem("activeTableOrders") || "{}");
          delete allOrders[tableId];
          localStorage.setItem("activeTableOrders", JSON.stringify(allOrders));
        }
        alert(`Bill ${generatedId} Settled and Saved to Database!`);
        window.print();
        navigate("/reports");
      }
    } catch (err) {
      alert("Error saving bill to database.");
    }
  };

  const handleSaveOrder = () => {
    if (!tableId) {
      alert("Please select a table from the Tables tab first.");
      return;
    }
    const allOrders = JSON.parse(localStorage.getItem("activeTableOrders") || "{}");
    allOrders[tableId] = billItems;
    localStorage.setItem("activeTableOrders", JSON.stringify(allOrders));
    alert(`Changes saved for Table ${tableId}`);
    navigate("/tables");
  };

  const updateQty = (item, delta) => {
    const existing = billItems.find(i => i.id === item.id);
    if (existing) {
      const newQty = existing.qty + delta;
      if (newQty <= 0) {
        setBillItems(billItems.filter(i => i.id !== item.id));
      } else {
        setBillItems(billItems.map(i => i.id === item.id ? { ...i, qty: newQty } : i));
      }
    } else if (delta > 0) {
      setBillItems([...billItems, { ...item, qty: 1 }]);
    }
  };

  if (showReceipt) {
    return (
      <div className="page-layout">
        <Navbar />
        <main className="main-content" style={{ background: '#FBF8F1', paddingTop: '40px' }}>
          <div className="receipt-paper">
            <div className="receipt-header-center">
              <h2 className="receipt-hotel-name">Dine Spark</h2>
              <p className="receipt-subtitle">Billing Receipt</p>
            </div>
            
            <div className="receipt-divider-top"></div>

            <div className="receipt-meta-row">
              <span>Table: {tableId || "Walk-in"}</span>
              <span>{new Date().toLocaleDateString()}, {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}</span>
            </div>

            <table className="receipt-table">
              <thead>
                <tr>
                  <th className="col-name">Item</th>
                  <th className="col-qty">Qty</th>
                  <th className="col-price">Price</th>
                  <th className="col-total">Total</th>
                </tr>
              </thead>
              <tbody>
                {billItems.map((item) => (
                  <tr key={item.id}>
                    <td className="col-name">{item.name}</td>
                    <td className="col-qty">{item.qty}</td>
                    <td className="col-price">₹{item.price}</td>
                    <td className="col-total">₹{item.price * item.qty}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="receipt-totals">
              <div className="receipt-row">
                <span>Subtotal</span>
                <span>₹{subtotal.toFixed(2)}</span>
              </div>
              
              {gst && (
                <div className="receipt-row">
                  <span>GST (5%)</span>
                  <span>₹{gstAmount.toFixed(2)}</span>
                </div>
              )}

              <div className="receipt-grand-total">
                <span>Grand Total</span>
                <span>₹{grandTotal.toFixed(2)}</span>
              </div>
            </div>

            <p className="thanks-msg">Thank you for dining with us!</p>

            <div className="receipt-actions">
              <button className="print-btn" onClick={handleSettleBill}>
                <Printer size={20} /> Print Bill
              </button>
              <button className="back-btn" onClick={() => setShowReceipt(false)}>
                <ArrowLeft size={20} /> Back
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="page-layout">
      <Navbar />
      <main className="main-content">
        <div className="billing-container">

        <header className="page-header">
          <div className="header-titles">
            <h1 className="serif-title">Billing</h1>
            <p className="subtitle">Create and manage orders</p>
          </div>
        </header>

        <div className="billing-grid">
          <div className="left-panel">
            <div className="top-box">
              <span className="select-table-label">Select Table: </span>
              <select 
                className="all-categories-pill table-select-dropdown" 
                value={tableId || "Choose table"}
                onChange={(e) => navigate(`/billing/${e.target.value}`)}
              >
                <option disabled value="Choose table">Choose table</option>
                {Array.from({ length: 15 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>Table {i + 1}</option>
                ))}
              </select>
            </div>

            <div className="search-row">
              <div className="search-wrapper">
                <Search size={18} className="search-icon-billing" />
                <input 
                  type="text" 
                  placeholder="Search menu items..." 
                  value={searchQuery} 
                  onChange={(e) => setSearchQuery(e.target.value)} 
                />
              </div>
              <select className="all-categories-pill" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                <option>All Categories</option>
                <option>Starter</option>
                <option>Main Course</option>
                <option>Dessert</option>
                <option>Beverage</option>
              </select>
            </div>

            <div className="menu-grid">
              {menuItems
                .filter(i => i.name.toLowerCase().includes(searchQuery.toLowerCase()) && (selectedCategory === "All Categories" || i.category === selectedCategory))
                .map(item => {
                  const cartItem = billItems.find(b => b.id === item.id);
                  return (
                    <div key={item.id} className="menu-card">
                      <div>
                        <div className="menu-name">{item.name}</div>
                        <div className="menu-price">₹{item.price}</div>
                      </div>
                      {!cartItem ? (
                        <button className="add-btn" onClick={() => updateQty(item, 1)}>
                          <Plus size={16} /> Add
                        </button>
                      ) : (
                        <div className="qty-controls">
                          <button onClick={() => updateQty(item, -1)}>−</button>
                          <span>{cartItem.qty}</span>
                          <button onClick={() => updateQty(item, 1)}>+</button>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>

          <div className="bill-card">
            <h2 className="summary-title">Order Summary</h2>
            <div className="bill-items-list">
              {billItems.length === 0 ? (
                <p className="empty-msg">No items added yet</p>
              ) : (
                billItems.map(item => (
                  <div key={item.id} className="bill-row">
                    <span>{item.name} × {item.qty}</span>
                    <span>₹{item.price * item.qty}</span>
                  </div>
                ))
              )}
            </div>
            
            <div className="bill-row subtotal-row" style={{ marginTop: '15px' }}>
              <span>Subtotal</span>
              <span>₹{subtotal}</span>
            </div>

            <div className="gst-box" onClick={() => setGst(!gst)}>
              <div className={`gst-circle ${gst ? "active" : ""}`} />
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                <span>Apply GST (5%)</span>
                {gst && <span style={{ fontWeight: '800', color: '#BA5028' }}>₹{gstAmount.toFixed(2)}</span>}
              </div>
            </div>

            <div className="grand-total-row">
              <span>Grand Total</span>
              <span>₹{grandTotal.toFixed(2)}</span>
            </div>

            <button className="bill-action-btn primary" onClick={() => billItems.length > 0 && setShowReceipt(true)}>
              <Printer size={18} /> Generate Bill
            </button>
            <button className="bill-action-btn secondary" onClick={handleSaveOrder}>
              <Save size={18} /> Save Order
            </button>
          </div>
        </div>
        </div>
      </main>
    </div>
  );
}